import {Platform, TextStyle} from 'react-native';
import {DefaultTheme} from '@react-navigation/native';
import {MuiPickersOverrides} from '@material-ui/pickers/typings/overrides';
import {DeviceSize, getFontSizeFactor, ThemeFunc} from './helpers';
import {createTheme} from '@material-ui/core/styles';
import {ThemeSettings} from './interface';
import {hexToRgbA} from '../pipes/color';
import {VisImpFontType, VisImpLetterSpacing, VisImpStatus, VisImpSwitchColor} from '../models/visuallyImpairedSettings';
import {ProfileApplicationsStatus} from '../models/profile/applications';
import {ProfileLinkedAccountStatus} from '../models/linkAccount';

const paletteDefault = {
  white: '#ffffff',
  textColor: '#333333',
  lightGrey: '#E5E5E5',
  lightOrange: '#F8A65A',
  ultraLightGrey: '#F7F7F7',
  primaryBlue: '#58B0DE',
  darkBlue: '#03479C',
  primaryGreen: '#00965A',
  grey: '#828282',
  middleGrey: '#C4C4C4',
  mercury: '#E7E7E7',
  black: '#000000',
  error: '#F44336',
  primaryOrange: '#ED801B',
  secondaryGray: '#252529',
  dobroRu: '#2B1263',
  cashbackIcon: '#be9e62',
  userIcon: '#26A673',
  chatbotIcon: '#ED801B',
};

const paletteWhite = {
  white: '#ffffff',
  textColor: '#333333',
  lightGrey: '#E5E5E5',
  lightOrange: '#ffffff',
  ultraLightGrey: '#F7F7F7',
  primaryBlue: '#000000',
  darkBlue: '#000000',
  primaryGreen: '#000000',
  grey: '#828282',
  middleGrey: '#C4C4C4',
  mercury: '#E7E7E7',
  black: '#000000',
  error: '#000000',
  primaryOrange: '#000000',
  secondaryGray: '#252529',
  dobroRu: '#2B1263',
  cashbackIcon: '#be9e62',
  userIcon: '#000000',
  chatbotIcon: '#000000',
};

const paletteBlack = {
  white: '#000000',
  textColor: '#E5E5E5',
  lightGrey: '#333333',
  lightOrange: '#333333',
  ultraLightGrey: '#333333',
  primaryBlue: '#ffffff',
  darkBlue: '#ffffff',
  primaryGreen: '#ffffff',
  grey: '#828282',
  middleGrey: '#E0E0E0',
  mercury: '#E7E7E7',
  black: '#ffffff',
  error: '#ffffff',
  primaryOrange: '#ffffff',
  secondaryGray: '#E5E5E5',
  dobroRu: '#2B1263',
  cashbackIcon: '#be9e62',
  userIcon: '#ffffff',
  chatbotIcon: '#ffffff',
};

const paletteBlue = {
  white: '#9dd1ff',
  textColor: '#14305e',
  lightGrey: '#cdcdcd',
  lightOrange: '#9dd1ff',
  ultraLightGrey: '#cdcdcd',
  primaryBlue: '#14305e',
  darkBlue: '#14305e',
  primaryGreen: '#14305e',
  grey: '#14305e',
  middleGrey: '#D0E8FF',
  mercury: '#14305e',
  black: '#14305e',
  error: '#14305e',
  primaryOrange: '#14305e',
  secondaryGray: '#14305e',
  dobroRu: '#2B1263',
  cashbackIcon: '#be9e62',
  userIcon: '#14305e',
  chatbotIcon: '#14305e',
};

const getPallete = (themeSettings: ThemeSettings) => {
  if (themeSettings.visuallyImpaired === VisImpStatus.Disabled) {
    return paletteDefault;
  }

  switch (themeSettings.switchColor) {
    case VisImpSwitchColor.Normal:
      return paletteWhite;
    case VisImpSwitchColor.Invert:
      return paletteBlack;
    case VisImpSwitchColor.Contrast:
      return paletteBlue;
  }
};

export interface FontFamily {
  light: string;
  regular: string;
  medium: string;
  bold: string;
}

const fontFamilies: {[style: string]: FontFamily} = {
  GothamPro: {
    light: 'GothamPro-Light',
    regular: 'GothamPro',
    medium: 'GothamPro-Medium',
    bold: 'GothamPro-Bold',
  },
  Arial: {
    light: 'Arial',
    regular: 'Arial',
    medium: 'Arial',
    bold: 'Arial',
  },
  'Times New Roman': {
    light: 'TimesNewRomanPSMT',
    regular: 'TimesNewRomanPSMT',
    medium: 'TimesNewRomanPSMT',
    bold: 'TimesNewRomanPS-BoldMT',
  },
};

const getFontFamilty = (visuallyImpairedActive: boolean, fontStyle: VisImpFontType): FontFamily => {
  if (!visuallyImpairedActive) {
    return fontFamilies.GothamPro;
  }
  if (fontStyle === VisImpFontType.SansSerif) {
    return fontFamilies.Arial;
  }
  if (fontStyle === VisImpFontType.Serif) {
    return fontFamilies['Times New Roman'];
  }
  console.error('This code should be unreachable');
  return fontFamilies.GothamPro;
};

export const getFontWeight = (fontWeight: TextStyle['fontWeight']): TextStyle['fontWeight'] => {
  return Platform.OS !== 'android' ? fontWeight : 'normal';
};

const defaultTheme: ThemeFunc = (align, themeSettings, deviceSize) => {
  const visuallyImpairedActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const fontFamily = getFontFamilty(visuallyImpairedActive, themeSettings.fontStyle);
  const fontSizeZoom = getFontSizeFactor(visuallyImpairedActive, themeSettings.fontSize);

  const letterSpacing =
    !visuallyImpairedActive || themeSettings.letterSpacing === VisImpLetterSpacing.Normal
      ? undefined
      : themeSettings.letterSpacing === VisImpLetterSpacing.Increased
      ? 1
      : 2;

  const palette = getPallete(themeSettings);

  const datePickerOverrides: MuiPickersOverrides = {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: palette.primaryOrange,
      },
    },
    MuiPickersCalendarHeader: {
      dayLabel: {
        fontSize: 15,
      },
    },
    MuiPickersDay: {
      day: {
        '&:hover': {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: palette.primaryOrange,
        },
        borderRadius: 5,
      },
      daySelected: {
        color: palette.white,
      },
      current: {
        '&:hover': {
          backgroundColor: palette.lightGrey,
        },
        backgroundColor: palette.lightGrey,
        color: palette.black,
      },
    },
  };

  const fonts = {
    fontFamily,
    h0: {
      fontSize: 70 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 67 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h1: {
      fontSize: 40 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 48 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h2: {
      fontSize: 30 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 45 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h2Light: {
      fontSize: 30 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 45 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    h3: {
      fontSize: 25 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 36 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h3Light: {
      fontSize: 24 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 36 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    h4: {
      fontSize: 22 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 28 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h4Light: {
      fontSize: 22 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 30 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    h5: {
      fontSize: 20 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 24 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    h5Light: {
      fontSize: 20 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 30 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    h6: {
      fontSize: 18 * fontSizeZoom,
      fontWeight: getFontWeight('700'),
      lineHeight: 24 * fontSizeZoom,
      fontFamily: fontFamily.bold,
      letterSpacing,
    },
    h6Light: {
      fontSize: 18 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 24 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    body: {
      fontSize: 16 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 24 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    action: {
      fontSize: 16 * fontSizeZoom,
      fontWeight: getFontWeight('700'),
      lineHeight: 22 * fontSizeZoom,
      fontFamily: fontFamily.bold,
      letterSpacing,
    },
    bodySmall: {
      fontSize: 14 * fontSizeZoom,
      fontWeight: getFontWeight('500'),
      lineHeight: 20 * fontSizeZoom,
      fontFamily: fontFamily.medium,
      letterSpacing,
    },
    bodySmallLight: {
      fontSize: 14 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 24 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    additionalText: {
      fontSize: 13 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 26 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    label: {
      fontSize: 12 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 16 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
    caption: {
      fontSize: 12 * fontSizeZoom,
      fontWeight: getFontWeight('300'),
      lineHeight: 16 * fontSizeZoom,
      fontFamily: fontFamily.light,
      letterSpacing,
    },
    small: {
      fontSize: 10 * fontSizeZoom,
      fontWeight: getFontWeight('normal'),
      lineHeight: 14 * fontSizeZoom,
      fontFamily: fontFamily.regular,
      letterSpacing,
    },
  };

  return {
    fonts,
    muiDefaultTheme: createTheme({
      typography: {
        fontFamily: fontFamily.regular,
        htmlFontSize: 15,
        body1: {
          fontSize: 20,
          lineHeight: 1.2,
        },
        body2: {
          fontSize: 20,
          lineHeight: 1.2,
        },
      },
      palette: {
        primary: {
          main: palette.primaryOrange,
          dark: palette.primaryOrange,
        },
      },
      overrides: datePickerOverrides as any,
    }),
    navigation: {
      ...DefaultTheme,
      colors: {
        ...DefaultTheme.colors,
        background: palette.ultraLightGrey,
        primary: palette.textColor,
      },
    },
    screen: {
      container: {
        [DeviceSize.mobile]: {
          backgroundColor: palette.ultraLightGrey,
        },
        [DeviceSize.tablet]: {
          backgroundColor: palette.ultraLightGrey,
        },
        [DeviceSize.desktop]: {
          backgroundColor: palette.ultraLightGrey,
        },
      },
      bgGradient: [palette.primaryBlue, palette.primaryGreen],
      bottomOffset: deviceSize === DeviceSize.desktop ? 100 : 50,
    },
    services: {
      bgGradient: ['#D6E4E9', '#C6DDC9'],
      itemBackground: palette.white,
    },
    button: {
      primary: {
        shadowColor: palette.black,
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        backgroundColor: palette.primaryOrange,
        disabled: {
          shadowOpacity: 0,
          shadowRadius: 0,
          backgroundColor: palette.lightGrey,
        },
      },
      secondary: {
        shadowColor: palette.primaryOrange,
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        backgroundColor: palette.white,
        borderColor: palette.primaryOrange,
        borderWidth: 1,
        disabled: {
          shadowOpacity: 0,
          shadowRadius: 0,
          backgroundColor: palette.lightGrey,
          borderWidth: 0,
        },
      },
      transparent: {
        backgroundColor: 'transparent',
        disabled: {
          backgroundColor: 'transparent',
        },
      },
    },
    welcome: {
      bgGradient: [palette.primaryBlue, palette.primaryGreen],
    },
    color: {
      grayMenu: palette.ultraLightGrey,
      backgroundMenuColor: palette.white,
      primaryColor: palette.primaryOrange,
      labelText: visuallyImpairedActive ? palette.textColor : palette.grey,
      textColor: palette.textColor,
      linkColor: visuallyImpairedActive ? palette.grey : palette.primaryOrange,
      primaryNonGreyBackground: palette.white,
      primaryBlackColor: palette.black,
      mainPageTextNameColor: palette.primaryGreen,
      borderColor: palette.lightGrey,
      nonActiveCheckBoxColor: palette.middleGrey,
      cardBackground: palette.lightOrange,
      cardText: visuallyImpairedActive ? palette.textColor : palette.white,
      iconGrayColor: palette.middleGrey,
      whiteTextColor: palette.white,
      shaddowBg: palette.middleGrey,
      patternBgColor: palette.mercury,
      loaderBgColor: palette.ultraLightGrey,
      profileAvatarIcon: visuallyImpairedActive ? palette.textColor : palette.grey,
      dobroRuIcon: palette.dobroRu,
      cashbackIcon: palette.cashbackIcon,
      userIcon: palette.userIcon,
      chatbotIcon: palette.chatbotIcon,
    },
    evenOddList: {
      evenContainer: hexToRgbA(palette.lightGrey, 0.15),
      oddContainer: palette.white,
    },
    checkbox: {
      markColor: palette.white,
      disabledColor: palette.grey,
      checkBorderColor: palette.grey,
      checkActiveColor: palette.primaryOrange,
    },
    buttonText: {
      primary: {
        fontSize: 18,
        lineHeight: 24,
        color: palette.white,
        fontFamily: fontFamily.regular,
      },
      secondary: {
        fontSize: 18,
        lineHeight: 24,
        color: palette.primaryOrange,
        fontFamily: fontFamily.regular,
      },
      transparent: {
        fontSize: 18,
        lineHeight: 24,
        color: palette.textColor,
        fontFamily: fontFamily.regular,
      },
      disabled: {
        primary: {
          color:
            visuallyImpairedActive &&
            (themeSettings.switchColor === VisImpSwitchColor.Invert ||
              themeSettings.switchColor === VisImpSwitchColor.Contrast)
              ? palette.grey
              : palette.white,
        },
        secondary: {
          color:
            visuallyImpairedActive &&
            (themeSettings.switchColor === VisImpSwitchColor.Invert ||
              themeSettings.switchColor === VisImpSwitchColor.Contrast)
              ? palette.grey
              : palette.white,
        },
        transparent: {
          color: palette.middleGrey,
        },
      },
    },
    helpText: {
      fontSize: fonts.label.fontSize,
      lineHeight: fonts.label.lineHeight,
      color: palette.grey,
    },
    html: {
      textRed:
        visuallyImpairedActive && themeSettings.switchColor === VisImpSwitchColor.Invert
          ? palette.black
          : palette.error,
    },
    input: {
      labelText: {
        color: palette.grey,
        fontSize: fonts.label.fontSize,
      },
      inputText: {
        fontSize: fonts.body.fontSize,
        color: palette.textColor,
        fontWeight: getFontWeight('normal'),
        fontFamily: fontFamily.regular,
        letterSpacing,
      },
      labelTextOutlined: {
        color: hexToRgbA(palette.grey, 0.8),
        fontSize: fonts.label.fontSize,
      },
      inputTextOutlined: {
        fontSize: fonts.body.fontSize,
        color: palette.textColor,
        fontWeight: getFontWeight('normal'),
        fontFamily: fontFamily.regular,
        letterSpacing,
      },
      common: {
        baseColor: palette.textColor,
        errorColor: palette.error,
        tintColor: palette.primaryOrange,
        hintColor: palette.lightGrey,
        controlLineWidth: 1,
        controlOutlinedWidth: 1,
      },
      variants: {
        outlined: {
          borderColor: palette.grey,
          disabledColor: palette.middleGrey,
        },
      },
      select: {
        wrap: {
          elevation: 2,
          zIndex: 999,
        },
        item: {
          paddingVertical: 12,
          paddingHorizontal: 16,
        },
      },
    },
    menu: {
      container: {},
      link: {
        marginLeft: align(24),
        marginRight: align(12),
      },
      activeLink: visuallyImpairedActive
        ? {
            backgroundColor: palette.primaryOrange,
            color: palette.white,
          }
        : {
            color: palette.primaryOrange,
          },
    },
    header: {
      container: {
        backgroundColor: palette.white,
      },
      topLineColor: palette.middleGrey,
      iconsColor: palette.primaryOrange,
      externalLinkColor: palette.primaryBlue,
      dropdownMenuSplitterColor: palette.lightGrey,
    },
    bottomMenu: {
      backgroundColor: palette.lightGrey,
      linkIconColor: palette.grey,
      activeLinkIconColor: palette.primaryOrange,
      centerLinkIconColor: palette.white,
      centerLinkIconGradient: [palette.primaryBlue, palette.primaryGreen],
    },
    drawerMenu: {
      profileContainer: {
        backgroundColor: palette.ultraLightGrey,
      },
      drawerItem: {
        borderBottomColor: palette.lightGrey,
        backgroundColor: 'transparent',
      },
      drawerItemBoldBorder: {
        borderBottomColor: palette.textColor,
      },
      drawerItemLabel: {
        // fontFamily: 'Gotham Pro',
        color: palette.textColor,
      },
      drawerItemLabelThin: {
        // fontFamily: 'Gotham Pro',
        color: palette.textColor,
      },
      activeDrawerItemLabel: {
        color: palette.primaryOrange,
      },
      logoutButtonLabel: {
        // fontFamily: 'Gotham Pro',
        fontStyle: 'normal',
        fontWeight: getFontWeight('normal'),
        fontSize: 16,
        lineHeight: 24,
        color: palette.textColor,
      },
    },
    topBar: {
      header: {
        backgroundColor: palette.ultraLightGrey,
        shadowColor: palette.black,
        shadowOpacity: 0.12,
        shadowOffset: {width: 0, height: 0},
        shadowRadius: 4,
      },
      headerTitle: {
        // fontFamily: 'Gotham Pro',
        color: palette.textColor,
      },
      headerLeftIcon: {
        color: palette.primaryOrange,
      },
    },
    hr: {
      borderStyle: 'solid',
      borderBottomColor: palette.middleGrey,
      borderBottomWidth: 1,
    },
    checkMarkList: {
      checkIcon: {
        color: palette.primaryOrange,
      },
    },
    privilegeNoData: {
      color: palette.grey,
    },
    privilegeCard: {
      backgroundColor: palette.white,
    },
    socialSupportMeasures: {
      link: {
        backgroundColor: palette.white,
        textColor: palette.textColor,
        shadowColor: palette.black,
      },
    },
    uscInfo: {
      bgGradient: ['#D6E4E9', '#C6DDC9'],
    },
    campus: {
      gradient: [hexToRgbA(palette.white, 0.4), hexToRgbA(palette.white, 0.1)],
      backgroundColor: '#F52D30',
      cardBackgroundColor: '#E2433B',
    },
    modal: {
      overlay: {
        backgroundColor: hexToRgbA(palette.black, 0.3),
      },
      wrap: {
        backgroundColor: palette.white,
      },
    },
    privilegeSsmResult: {
      backgroundColor: palette.primaryGreen,
      color: palette.white,
    },
    applications: {
      textCaptionColor: palette.grey,
      statusIndicatorColor: palette.primaryOrange,
      tableRowSeparatorColor: palette.lightGrey,
      mobileHistoryStatusBg: palette.white,
      labelColor: palette.grey,
      statusColors: {
        [ProfileApplicationsStatus.accepted]: palette.darkBlue,
        [ProfileApplicationsStatus.review]: palette.primaryOrange,
        [ProfileApplicationsStatus.approved]: palette.primaryGreen,
        [ProfileApplicationsStatus.rejected]: palette.error,
      },
      mobileListItemBodyBg: palette.lightGrey,
    },
    linkedAccounts: {
      textCaptionColor: palette.grey,
      statusIndicatorColor: palette.primaryOrange,
      tableRowSeparatorColor: palette.lightGrey,
      mobileHistoryStatusBg: palette.white,
      labelColor: palette.grey,
      statusColors: {
        [ProfileLinkedAccountStatus.Accepted]: palette.primaryGreen,
        [ProfileLinkedAccountStatus.Pending]: palette.primaryOrange,
        [ProfileLinkedAccountStatus.acceptedOld]: palette.primaryGreen,
        [ProfileLinkedAccountStatus.rejectedOld]: palette.error,
      },
      mobileListItemBodyBg: palette.lightGrey,
    },
    profileCard: {
      cardBorderColor: palette.lightGrey,
      traceryColor: palette.lightGrey,
      cardBackground: palette.white,
      cardWrapShadow: {
        shadowColor: palette.black,
        shadowOpacity: 0.1,
        shadowOffset: {width: 2, height: 2},
        shadowRadius: 10,
      },
      cardNumber: {
        fontSize: 22,
        lineHeight: 28,
        letterSpacing: 0.8,
        fontWeight: getFontWeight('500'),
        fontFamily: fontFamily.bold,
      },
    },
    accordion: {
      itemFilledBoxShadow: {
        shadowColor: palette.black,
        shadowOpacity: 0.1,
        shadowOffset: {width: 2, height: 2},
        shadowRadius: 10,
      },
      headerBgColor: palette.lightOrange,
      headerTextColor: visuallyImpairedActive ? palette.textColor : palette.white,
      itemContentBgColor: palette.white,
      itemOutlinedBorderColor: visuallyImpairedActive ? palette.white : palette.lightGrey,
    },
    supportService: {
      cardBgColor: palette.white,
      cardBoxShadow: {
        shadowColor: palette.black,
        shadowOpacity: 0.14,
        shadowOffset: {width: 0, height: 2},
        shadowRadius: 4,
      },
      cardTraceryColor: palette.lightGrey,
      phoneColor: palette.primaryOrange,
      questionAccordionCheckMarkColor: palette.primaryOrange,
      iconsColor: {
        transport: palette.primaryBlue,
        banking: palette.primaryOrange,
        babyFood: palette.darkBlue,
        privileges: visuallyImpairedActive ? palette.textColor : palette.primaryGreen,
        loyalty: visuallyImpairedActive ? palette.textColor : '#5AA8B4',
        socialSupport: visuallyImpairedActive ? palette.textColor : '#9ADDE1',
        volunteers: visuallyImpairedActive ? palette.textColor : '#42945d',
        rewardShop: visuallyImpairedActive ? palette.textColor : palette.lightOrange,
        certificates: visuallyImpairedActive ? palette.textColor : '#5AA8B4',
        skipass: palette.primaryBlue,
      },
      chatbotScreen: {
        bgGradient1: ['#D6E4E9', '#C6DDC9'],
        bgGradient2: ['rgba(255, 255, 255, 0.3)', 'rgba(255, 255, 255, 0.3)'],
      },
    },
    skipass: {
      greenColor: palette.primaryGreen,
      redColor: palette.error,
      neutralColor: palette.textColor,
    },
    qrCodeScreen: {
      bgGradient: ['#D6E4E9', '#C6DDC9'],
    },
    supportServiceCreateTopicForm: {
      filePickerHintColor: palette.grey,
    },
    filePicker: {
      clipColor: palette.primaryOrange,
      removeFileIconColor: palette.black,
    },
    snackbar: {
      colors: {
        success: {
          backgroundColor: palette.primaryGreen,
        },
        error: {
          backgroundColor: palette.error,
        },
        warning: {
          backgroundColor: palette.primaryOrange,
        },
        info: {
          backgroundColor: palette.middleGrey,
        },
      },
    },
    polls: {
      shapes: {
        color: palette.middleGrey,
      },
      voteResult: {
        itemBg: palette.ultraLightGrey,
        itemActiveBg: palette.primaryOrange,
        itemBgMobile: palette.lightGrey,
        itemActiveBgMobile: palette.lightOrange,
      },
    },
    footer: {
      bgGradient: [palette.primaryBlue, palette.primaryGreen],
      bottomLineColor: visuallyImpairedActive ? palette.textColor : palette.white,
      textColor: visuallyImpairedActive ? palette.textColor : palette.white,
      shapeColor: hexToRgbA(palette.white, 0.15),
    },
  };
};

export default defaultTheme;
